import React from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../../components/common/Layout';
import { posts } from '../../constants/posts'; // Assuming posts are defined here
import { ArrowLeft } from 'lucide-react';
import { Helmet } from 'react-helmet';
import ReactMarkdown from 'react-markdown';
import SocialShare from '../../components/common/SocialShare';
import { useEffect } from 'react';
import RelatedResources from './RelatedResources';
import TextToSpeechPlayer from './TextToSpeechPlayer';


// Convert single newlines inside paragraphs to Markdown hard line breaks (two spaces + \n),
// while preserving paragraph breaks (double newlines). This avoids adding plugins/deps.
const toHardBreaks = (md: string) =>
  md
    .replace(/\r\n/g, '\n')
    .split(/\n{2,}/)
    .map((p) => p.replace(/\n/g, '  \n'))
    .join('\n\n');

// Optimization: Define components outside render to verify reference stability and avoid unnecessary re-renders
const MarkdownComponents: React.ComponentProps<typeof ReactMarkdown>['components'] = {
  p: ({ ...props }) => <p className="text-gray-900 leading-relaxed mb-4" {...props} />,
  h2: ({ ...props }) => <p className="text-gray-950 mb-2 font-semibold" {...props} />,
  ul: ({ ...props }) => <ul className="list-disc list-inside mb-6 ml-4" {...props} />,
  ol: ({ ...props }) => <ol className="list-decimal list-inside mb-2 ml-8" {...props} />,
  li: ({ ...props }) => <li className="mb-1" {...props} />,
  blockquote: ({ ...props }) => (
    <blockquote className="border-l-4 border-contessa-400 pl-4 italic mb-8 mt-6" {...props} />
  ),
  a: ({ ...props }) => (
    <a
      className="text-contessa-600 font-semibold underline decoration-contessa-400 hover:text-contessa-500 transition"
      target="_blank"
      rel="noreferrer"
      {...props}
    />
  ),
};

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = posts.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!post) {
    return (
      <Layout>
        <Helmet>
          <title>Post no encontrado | Los Ciclos de Marha</title>
          <meta name="description" content="Lo siento, la publicación que buscas no existe." />
        </Helmet>
        <section id="blog" className="pt-24 pb-16 md:py-28 lg:py-32">
          <div className="container mx-auto px-4 py-8 text-center">
            <h2 className="text-3xl font-bold mb-4 text-contessa-600">Post no encontrado</h2>
            <p className="mb-8 text-contessa-700">Lo sentimos, la publicación que buscas no existe.</p>
            <Link to="/blog" className="text-blue-500 hover:underline">Volver al Blog</Link>
          </div>
        </section>
      </Layout>
    )
  }

  const currentUrl = `https://losciclosdemarha.uy/blog/${post.slug}`;
  const imageUrl = `https://losciclosdemarha.uy${post.image}`;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "image": [imageUrl],
    "datePublished": post.date, // Note: For better SEO, ISO 8601 format (YYYY-MM-DD) is preferred if available
    "author": [{
        "@type": "Person",
        "name": "Marha Scanu",
        "url": "https://losciclosdemarha.uy/about"
      }],
    "publisher": {
      "@type": "Organization",
      "name": "Los Ciclos de Marha",
      "logo": {
        "@type": "ImageObject",
        "url": "https://losciclosdemarha.uy/images/marha_scanu.avif" // Using a generic logo/image as fallback
      }
    },
    "description": post.excerpt
  };

  return (
    <Layout>
      <Helmet>
        <title>{post.title} | Los Ciclos de Marha</title>
        <meta name="description" content={post.excerpt || `Lee más sobre ${post.title}`} />
        <link rel="canonical" href={currentUrl} />
        
        {/* Open Graph / Facebook / WhatsApp */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt || `Lee más sobre ${post.title}`} />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={post.imageAlt || post.title} />
        <meta property="og:site_name" content="Los Ciclos de Marha" />
        <meta property="og:locale" content="es_UY" />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:author" content="Marha Scanu" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt || `Lee más sobre ${post.title}`} />
        <meta name="twitter:image" content={imageUrl} />

        {/* Structured Data for SEO */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      <section id="blog" className="pt-24 pb-16 md:py-28 lg:py-32">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <article className="prose lg:prose-xl">
            <h1 className="text-4xl font-bold mb-1 text-contessa-800">{post.title}</h1>
            <p className="text-contessa-500 mb-6 text-sm">Publicado el {post.date}</p>
            {/* Mobile: player above image */}
            <div className="md:hidden">
              <TextToSpeechPlayer text={post.content} />
            </div>
            <figure className="mb-6">
              <img src={post.image} alt={post.imageAlt || post.title} className="rounded-lg shadow-md" />
              {(post.imageAlt || post.imageAuthor) && (
                <figcaption className="mt-1 text-right text-xs italic text-contessa-600 space-y-0.5">
                  {post.imageAlt && <p>{post.imageAlt}</p>}
                  {post.imageAuthor && (
                    <p>
                      Foto:{' '}
                      {post.imageAuthorUrl ? (
                        <a
                          href={post.imageAuthorUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="underline decoration-contessa-400 hover:text-contessa-500 transition"
                        >
                          {post.imageAuthor}
                        </a>
                      ) : (
                        <span>{post.imageAuthor}</span>
                      )}
                    </p>
                  )}
                </figcaption>
              )}
            </figure>
            {/* Desktop: player below image */}
            <div className="hidden md:block">
              <TextToSpeechPlayer text={post.content} />
            </div>
            <div className="items-center mb-6">
              <ReactMarkdown components={MarkdownComponents}>
                {toHardBreaks(post.content)}
              </ReactMarkdown>
            </div>
          </article>
          <SocialShare
            title={post.title}
            url={currentUrl}
            description={post.excerpt}
            image={post.image}
          />
          
          <RelatedResources resourceIds={post.relatedResourceIds} />

          <div className="mt-12 text-center">
            <Link to="/blog" className="hover:underline flex items-center justify-center text-contessa-700">
              <ArrowLeft className="h-5 w-5 mr-2" />
              Volver al Blog
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  )

};

export default BlogPostPage;
